/**
 * Shared FCM / push notification routing contract for the hub shell.
 *
 * Backend payloads put routing fields in the FCM `data` map (all values are strings).
 * Supported types today:
 *
 * **Chatty channel messages** (`channel_message`):
 *
 * ```json
 * {
 *   "type": "channel_message",
 *   "channel_id": "<uuid>",
 *   "message_id": "<uuid>",
 *   "sender_id": "<uuid>",
 *   "channel_type": "group|direct"
 * }
 * ```
 *
 * **Documents file changes** (`file_changed`):
 *
 * ```json
 * {
 *   "type": "file_changed",
 *   "event": "file_uploaded|file_revision_created",
 *   "folder_id": "45",
 *   "file_id": "123",
 *   "file_name": "doc.pdf",
 *   "version": "2"
 * }
 * ```
 *
 * Future sub-apps can extend {@link resolvePushHubDestination} (e.g. `type: "breakdown"`
 * → machines breakdown detail). Hub listens for Capacitor `pushNotificationActionPerformed`
 * and navigates using the returned hub path + embedded `subPath`.
 */

/** Raw FCM data map — values are always strings per FCM HTTP v1. */
export type PushDataRecord = Record<string, string>;

/** Minimum shape every routed push must include. */
export type PushDataBase = PushDataRecord & {
	type: string;
};

/** Chatty new-message notification (`NewChannelMessageNotification`). */
export type ChannelMessagePushData = PushDataBase & {
	type: 'channel_message';
	channel_id: string;
	message_id?: string;
	sender_id?: string;
	channel_type?: string;
};

/** DocuMassi file changed notification (`FileChangedDbNotification` FCM data). */
export type FileChangedPushData = PushDataBase & {
	type: 'file_changed';
	folder_id: string;
	file_id: string;
	event?: string;
	file_name?: string;
	version?: string;
};

/**
 * Hub navigation target derived from a push `data` payload.
 * `subPath` is passed to `AppHandle.syncPath` when the sub-app is already mounted.
 */
export type PushHubDestination = {
	/** Absolute hub router path (e.g. `/apps/chat/c/{id}`). */
	hubPath: string;
	/** Hub app id (`chat`, `machines`, …). */
	appId: string;
	/** Path relative to the embedded app base (e.g. `c/{id}`). */
	subPath: string;
	/** Keycloak realm role required to open the destination. */
	requiredRole: string;
};

function asStringRecord(data: unknown): PushDataRecord {
	if (!data || typeof data !== 'object') {
		return {};
	}

	const out: PushDataRecord = {};

	for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
		if (value == null) {
			continue;
		}

		out[key] = typeof value === 'string' ? value : String(value);
	}

	return out;
}

/**
 * Map FCM notification `data` to a hub deep link.
 * Returns `null` when the payload is missing, unknown, or incomplete.
 */
export function resolvePushHubDestination(data: unknown): PushHubDestination | null {
	const record = asStringRecord(data);
	const type = record.type?.trim();

	if (!type) {
		return null;
	}

	if (type === 'channel_message') {
		const channelId = record.channel_id?.trim();
		if (!channelId) {
			return null;
		}

		const subPath = `c/${channelId}`;

		return {
			hubPath: `/apps/chat/${subPath}`,
			appId: 'chat',
			subPath,
			requiredRole: 'app:chat',
		};
	}

	if (type === 'file_changed') {
		const folderId = record.folder_id?.trim();
		const fileId = record.file_id?.trim();
		if (!folderId || !fileId) {
			return null;
		}

		const subPath = `dashboard?folder=${folderId}&file=${fileId}`;

		return {
			hubPath: `/apps/documents/${subPath}`,
			appId: 'documents',
			subPath,
			requiredRole: 'app:documents',
		};
	}

	// Future: type === 'breakdown' → machines breakdown detail by id.
	return null;
}
